'use client'

import {
  Button,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ArtifactCanvasActionRow,
  formatRelativeTimeIso,
} from '@yeshuman/sdk'
import { useState } from 'react'
import { Bell, CheckCircle2, MessageSquare, Send, XCircle, type LucideIcon } from 'lucide-react'
import { InviteToApplyAction } from '../invite-to-apply-action'
import type { Stage } from '../../../hooks/use-opportunity-applicants'
import type { CandidatePanelSource } from './candidate-panel-types'
import {
  isCandidateActiveApplicant,
  isCandidateInterviewStage,
  isCandidateTerminal,
  resolveCandidateCommitment,
} from './candidate-panel-types'

const DECISION_ACTIONS: {
  id: 'hired' | 'offer' | 'rejected'
  label: string
  icon: LucideIcon
  idleVariant: 'outline' | 'outlineDestructive'
  activeVariant: 'default' | 'destructive'
}[] = [
  { id: 'hired', label: 'Hired', icon: CheckCircle2, idleVariant: 'outline', activeVariant: 'default' },
  { id: 'offer', label: 'Extend offer', icon: Send, idleVariant: 'outline', activeVariant: 'default' },
  {
    id: 'rejected',
    label: 'Reject',
    icon: XCircle,
    idleVariant: 'outlineDestructive',
    activeVariant: 'destructive',
  },
]

export type CandidateActionBarProps = {
  source: CandidatePanelSource
  stages: Stage[]
  isUpdatingStage?: boolean
  isRecordingDecision?: boolean
  isInviting?: boolean
  nudgePending?: boolean
  onChangeStage?: (stageSlug: string) => void
  onRecordDecision?: (decision: 'hired' | 'rejected' | 'offer', reason?: string) => void
  onInvite?: (profileId: string) => void
  onNudge?: (applicationId: string) => void
  onOpenThread?: () => void
  onScheduleInterview?: () => void
  applied?: boolean
  invited?: boolean
}

export function CandidateActionBar({
  source,
  stages,
  isUpdatingStage,
  isRecordingDecision,
  isInviting,
  nudgePending,
  onChangeStage,
  onRecordDecision,
  onInvite,
  onNudge,
  onOpenThread,
  onScheduleInterview,
  applied,
  invited,
}: CandidateActionBarProps) {
  const commitment = resolveCandidateCommitment(source)
  const terminal = isCandidateTerminal(source)
  const activeApplicant = isCandidateActiveApplicant(source)
  const interviewStage = isCandidateInterviewStage(source)
  const [decisionReason, setDecisionReason] = useState('')
  const [pendingDecision, setPendingDecision] = useState<'hired' | 'rejected' | 'offer' | null>(null)

  function submitDecision() {
    if (!pendingDecision || !onRecordDecision) return
    onRecordDecision(pendingDecision, decisionReason.trim() || undefined)
    setDecisionReason('')
    setPendingDecision(null)
  }

  if (source.kind === 'prospect') {
    const profileId = source.match.profile_id
    if (!onInvite) return null
    return (
      <ArtifactCanvasActionRow aria-label="Prospect actions">
        <InviteToApplyAction
          profileId={profileId}
          isApplied={applied ?? false}
          isInvited={invited ?? false}
          isInviting={isInviting ?? false}
          onInvite={onInvite}
        />
      </ArtifactCanvasActionRow>
    )
  }

  if (source.kind !== 'applicant') return null

  const applicant = source.applicant

  if (commitment === 'invited') {
    // TODO(withdraw-invite): employer-side withdraw has no endpoint today.
    const nudgedLabel = applicant.nudged_at
      ? `Nudged ${formatRelativeTimeIso(applicant.nudged_at)}`
      : null
    return (
      <ArtifactCanvasActionRow aria-label="Invite actions">
        {nudgedLabel ? (
          <span className="text-sm text-muted-foreground">{nudgedLabel}</span>
        ) : onNudge ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            pressFeedback
            disabled={nudgePending}
            onClick={() => onNudge(applicant.id)}
          >
            <Bell className="mr-1.5 shrink-0" aria-hidden />
            Nudge
          </Button>
        ) : null}
      </ArtifactCanvasActionRow>
    )
  }

  if (terminal) {
    return (
      <p className="text-sm text-muted-foreground">
        {applicant.current_stage?.name ?? applicant.status.replace(/_/g, ' ')} — read-only
      </p>
    )
  }

  if (!activeApplicant) return null

  const currentSlug = applicant.current_stage?.slug ?? ''
  const nonTerminalStages = stages.filter((stage) => !stage.is_terminal)

  return (
    <div className="space-y-3">
      <ArtifactCanvasActionRow aria-label="Applicant actions">
        {onChangeStage ? (
          <Select
            value={currentSlug}
            onValueChange={(value) => {
              if (!value || value === currentSlug) return
              onChangeStage(value)
            }}
            disabled={isUpdatingStage}
          >
            <SelectTrigger className="h-8 w-auto min-w-[8.5rem]">
              <SelectValue placeholder="Stage…" />
            </SelectTrigger>
            <SelectContent>
              {nonTerminalStages.map((stage) => (
                <SelectItem key={stage.slug} value={stage.slug}>
                  {stage.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        {interviewStage && onScheduleInterview ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            pressFeedback
            onClick={onScheduleInterview}
          >
            Schedule interview
          </Button>
        ) : null}
        {applicant.has_thread && onOpenThread ? (
          <Button type="button" variant="outline" size="sm" pressFeedback onClick={onOpenThread}>
            <MessageSquare className="h-4 w-4" />
            Open thread
          </Button>
        ) : null}
        {onRecordDecision
          ? DECISION_ACTIONS.map(({ id, label, icon: Icon, idleVariant, activeVariant }) => {
              const isActive = pendingDecision === id
              return (
                <Button
                  key={id}
                  variant={isActive ? activeVariant : idleVariant}
                  size="sm"
                  pressFeedback
                  onClick={() => setPendingDecision(id)}
                  disabled={isRecordingDecision}
                >
                  <Icon className="h-4 w-4" /> {label}
                </Button>
              )
            })
          : null}
      </ArtifactCanvasActionRow>
      {onRecordDecision && pendingDecision ? (
        <div className="space-y-2 rounded-md border border-border/60 bg-muted/20 p-4">
          <label className="text-xs font-medium text-muted-foreground">
            Reason <span className="text-muted-foreground/70">(optional)</span>
          </label>
          <Textarea
            value={decisionReason}
            onChange={(e) => setDecisionReason(e.target.value)}
            placeholder="Share context for the decision…"
            className="min-h-[72px]"
            disabled={isRecordingDecision}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setPendingDecision(null)
                setDecisionReason('')
              }}
              disabled={isRecordingDecision}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={submitDecision}
              disabled={isRecordingDecision}
              variant={pendingDecision === 'rejected' ? 'destructive' : 'default'}
            >
              {isRecordingDecision ? 'Saving…' : 'Confirm decision'}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
